/**
 * Taxi Interior - passenger-side cabin at night, city light passing the window.
 * The cabin frame is static; only the world outside the glass moves, plus a
 * gentle suspension sway that completes whole cycles per loop.
 */

import { createRng, drawGrain, drawVignette, glow, reactive, withAlpha } from './shared.js';

const WORLD = (() => {
  const rng = createRng(0x7a31c);

  const lights = Array.from({ length: 34 }, () => ({
    ny: rng(),
    cycles: 1 + Math.floor(rng() * 3),
    offset: rng(),
    length: 0.06 + rng() * 0.22,
    thickness: 0.002 + rng() * 0.006,
    warm: rng(),
    alpha: 0.4 + rng() * 0.6,
  }));

  const signs = Array.from({ length: 7 }, () => ({
    ny: 0.1 + rng() * 0.6,
    cycles: 1 + Math.floor(rng() * 2),
    offset: rng(),
    radius: 0.06 + rng() * 0.12,
    warm: rng(),
    brightness: 0.25 + rng() * 0.4,
  }));

  const droplets = Array.from({ length: 30 }, () => ({
    nx: rng(),
    ny: rng(),
    r: 0.0018 + rng() * 0.005,
    alpha: 0.08 + rng() * 0.2,
  }));

  return { lights, signs, droplets };
})();

const WINDOW = { x: 0.05, y: 0.14, w: 0.9, h: 0.5 };

export default {
  id: 'taxi-interior',
  label: 'Taxi Interior',
  description: 'Procedural taxi cabin with city lights sliding past the window.',
  loopable: true,

  draw(ctx, { t, loopSeconds, w, h, audio, strength }) {
    const phase = (t % loopSeconds) / loopSeconds;
    const bass = reactive(audio.bass, strength);
    const level = reactive(audio.level, strength);

    // --- cabin base
    ctx.fillStyle = '#0A0B0F';
    ctx.fillRect(0, 0, w, h);

    // --- suspension sway (2 full cycles per loop -> seamless)
    const swayX = Math.sin(phase * Math.PI * 4) * w * 0.004 * (1 + bass * 0.5);
    const swayY = Math.sin(phase * Math.PI * 2) * h * 0.0025 * (1 + bass * 0.5);
    ctx.save();
    ctx.translate(swayX, swayY);

    const wx = WINDOW.x * w;
    const wy = WINDOW.y * h;
    const ww = WINDOW.w * w;
    const wh = WINDOW.h * h;
    const radius = Math.min(ww, wh) * 0.12;

    // --- world outside the glass
    ctx.save();
    ctx.beginPath();
    roundRect(ctx, wx, wy, ww, wh, radius);
    ctx.clip();

    const sky = ctx.createLinearGradient(wx, wy, wx, wy + wh);
    sky.addColorStop(0, '#080C14');
    sky.addColorStop(0.55, '#131A26');
    sky.addColorStop(1, '#0A0D13');
    ctx.fillStyle = sky;
    ctx.fillRect(wx, wy, ww, wh);

    // soft shop-sign blobs drifting past
    for (const s of WORLD.signs) {
      const travel = (s.offset + phase * s.cycles) % 1.2;
      const x = wx + (1.2 - travel) * ww * 1.2 - ww * 0.1;
      const color = s.warm > 0.6 ? '#FF9A3C' : s.warm > 0.3 ? '#FFD08A' : '#7FB4FF';
      glow(ctx, x, wy + s.ny * wh, s.radius * ww, color, s.brightness * (0.75 + level * 0.5));
    }

    // streaking street lights
    for (const l of WORLD.lights) {
      const travel = (l.offset + phase * l.cycles) % 1.15;
      const x = wx + (1.15 - travel) * ww * 1.15 - ww * 0.15;
      const y = wy + l.ny * wh;
      const len = l.length * ww;
      const color = l.warm > 0.7 ? '#FFB066' : l.warm > 0.4 ? '#FFE3B8' : '#BBD6FF';
      const gradient = ctx.createLinearGradient(x, 0, x + len, 0);
      gradient.addColorStop(0, withAlpha(color, 0));
      gradient.addColorStop(0.45, withAlpha(color, l.alpha * (0.8 + level * 0.35)));
      gradient.addColorStop(1, withAlpha(color, 0));
      ctx.fillStyle = gradient;
      ctx.fillRect(x, y, len, Math.max(1.2, l.thickness * wh));
    }

    // glass tint + static rain flecks
    ctx.fillStyle = 'rgba(90,120,160,0.07)';
    ctx.fillRect(wx, wy, ww, wh);
    for (const d of WORLD.droplets) {
      ctx.fillStyle = withAlpha('#DCEBFF', d.alpha);
      ctx.beginPath();
      ctx.arc(wx + d.nx * ww, wy + d.ny * wh, Math.max(0.8, d.r * w), 0, Math.PI * 2);
      ctx.fill();
    }
    // diagonal reflection band
    const refl = ctx.createLinearGradient(wx, wy + wh, wx + ww, wy);
    refl.addColorStop(0.35, 'rgba(255,255,255,0)');
    refl.addColorStop(0.5, `rgba(255,255,255,${0.03 + level * 0.03})`);
    refl.addColorStop(0.65, 'rgba(255,255,255,0)');
    ctx.fillStyle = refl;
    ctx.fillRect(wx, wy, ww, wh);
    ctx.restore();

    // --- window frame
    ctx.strokeStyle = '#05060A';
    ctx.lineWidth = Math.max(6, w * 0.028);
    ctx.beginPath();
    roundRect(ctx, wx, wy, ww, wh, radius);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = Math.max(1, w * 0.003);
    ctx.beginPath();
    roundRect(ctx, wx + w * 0.014, wy + h * 0.01, ww - w * 0.028, wh - h * 0.02, radius * 0.85);
    ctx.stroke();

    // --- roof
    const roof = ctx.createLinearGradient(0, 0, 0, wy + h * 0.04);
    roof.addColorStop(0, '#05060A');
    roof.addColorStop(1, '#101218');
    ctx.fillStyle = roof;
    ctx.fillRect(0, 0, w, wy + h * 0.04);

    // --- seat headrest silhouette, lower left
    ctx.fillStyle = '#07080C';
    ctx.beginPath();
    ctx.moveTo(-w * 0.02, h);
    ctx.lineTo(-w * 0.02, h * 0.66);
    ctx.quadraticCurveTo(w * 0.1, h * 0.6, w * 0.26, h * 0.7);
    ctx.lineTo(w * 0.34, h);
    ctx.closePath();
    ctx.fill();

    // --- dashboard / door panel
    const dash = ctx.createLinearGradient(0, h * 0.66, 0, h);
    dash.addColorStop(0, '#0E1016');
    dash.addColorStop(0.4, '#080A0E');
    dash.addColorStop(1, '#040508');
    ctx.fillStyle = dash;
    ctx.fillRect(0, h * 0.66, w, h * 0.34);

    // dashboard instrument glow
    glow(ctx, w * 0.72, h * 0.78, w * 0.3, '#FF8A2B', 0.1 + bass * 0.18 + level * 0.06);
    glow(ctx, w * 0.24, h * 0.8, w * 0.22, '#3E6CFF', 0.05 + bass * 0.1);

    ctx.restore();

    drawVignette(ctx, w, h, 0.62);
    drawGrain(ctx, w, h, { t, alpha: 0.032 });
  },
};

/** Local rounded-rect path helper (kept here so shared.js stays generic). */
function roundRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
}
